import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Nav, Pagination, Row, Spinner, Table } from "react-bootstrap";
import axios from "axios";
import { TranslatorContext } from "../../context/Translator";
import PageLayout from "../../layouts/PageLayout";
import SubmitPaymentModal from "./SubmitPaymentModal";

const PAGE_SIZE = 20;

const getAuthConfig = () => ({
    headers: {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        Accept: "application/json",
    },
});

const getPagedResult = (response, page) => {
    const paginator = response.data?.data;
    return {
        rows: Array.isArray(paginator?.data) ? paginator.data : [],
        pagination: {
            currentPage: paginator?.current_page || page,
            lastPage: paginator?.last_page || 1,
            total: paginator?.total || 0,
            from: paginator?.from || 0,
            to: paginator?.to || 0,
        },
    };
};

const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? "-"
        : date.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

const formatAmount = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount)
        ? amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : "0.00";
};

const getPaymentStatus = (payment) => {
    const status = String(payment.status || payment.payment_type || "").trim();
    return status ? status.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase()) : "-";
};

const getPaymentStatusVariant = (payment) => {
    const status = String(payment.status || payment.payment_type || "").toLowerCase();
    if (status === "credit" || status === "received" || status === "paid") return "success";
    if (status === "debit") return "primary";
    return "secondary";
};

const getStatusVariant = (status) => {
    switch ((status || "").toLowerCase()) {
        case "delivered":
            return "success";
        case "pending":
            return "warning";
        case "returned":
            return "danger";
        default:
            return "secondary";
    }
};

function LedgerPagination({ pagination, onPageChange, label }) {
    if (pagination.lastPage <= 1) return null;

    const pages = [];
    const firstVisible = Math.max(1, pagination.currentPage - 1);
    const lastVisible = Math.min(pagination.lastPage, pagination.currentPage + 1);

    pages.push(
        <Pagination.Prev
            key="previous"
            disabled={pagination.currentPage <= 1}
            onClick={() => onPageChange(pagination.currentPage - 1)}
        />
    );
    if (firstVisible > 1) {
        pages.push(<Pagination.Item key={1} onClick={() => onPageChange(1)}>1</Pagination.Item>);
        if (firstVisible > 2) pages.push(<Pagination.Ellipsis key="left-ellipsis" disabled />);
    }
    for (let page = firstVisible; page <= lastVisible; page += 1) {
        pages.push(
            <Pagination.Item
                key={page}
                active={page === pagination.currentPage}
                onClick={() => onPageChange(page)}
            >
                {page}
            </Pagination.Item>
        );
    }
    if (lastVisible < pagination.lastPage) {
        if (lastVisible < pagination.lastPage - 1) {
            pages.push(<Pagination.Ellipsis key="right-ellipsis" disabled />);
        }
        pages.push(
            <Pagination.Item
                key={pagination.lastPage}
                onClick={() => onPageChange(pagination.lastPage)}
            >
                {pagination.lastPage}
            </Pagination.Item>
        );
    }
    pages.push(
        <Pagination.Next
            key="next"
            disabled={pagination.currentPage >= pagination.lastPage}
            onClick={() => onPageChange(pagination.currentPage + 1)}
        />
    );

    return (
        <Card.Footer className="bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
            <small className="text-muted">
                {label}: {pagination.from || 0}-{pagination.to || 0} of {pagination.total}
            </small>
            <Pagination size="sm" className="mb-0">{pages}</Pagination>
        </Card.Footer>
    );
}

export default function MplacerLedgerPage() {
    const { t } = useContext(TranslatorContext);
    const [activeTab, setActiveTab] = useState("orders");
    const [activeOrderTab, setActiveOrderTab] = useState("delivered");
    const [summary, setSummary] = useState(null);
    const [showSubmitPayment, setShowSubmitPayment] = useState(false);
    const [notice, setNotice] = useState("");
    const [orders, setOrders] = useState([]);
    const [payments, setPayments] = useState([]);
    const [ordersPagination, setOrdersPagination] = useState({
        currentPage: 1, lastPage: 1, total: 0, from: 0, to: 0,
    });
    const [paymentsPagination, setPaymentsPagination] = useState({
        currentPage: 1, lastPage: 1, total: 0, from: 0, to: 0,
    });
    const [ordersPage, setOrdersPage] = useState(1);
    const [paymentsPage, setPaymentsPage] = useState(1);
    const [orderSearch, setOrderSearch] = useState("");
    const [paymentSearch, setPaymentSearch] = useState("");
    const [paymentFromDate, setPaymentFromDate] = useState("");
    const [paymentToDate, setPaymentToDate] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const loadOrders = useCallback(async (page) => {
        setLoading(true);
        setError("");
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/market-placer/my-ledger`,
                { ...getAuthConfig(), params: { page, per_page: PAGE_SIZE } }
            );
            if (!response.data?.success) {
                throw new Error(response.data?.message || "Failed to load ledger orders.");
            }
            const result = getPagedResult(response, page);
            setOrders(result.rows);
            setOrdersPagination(result.pagination);
        } catch (err) {
            console.error("Error fetching market placer ledger orders:", err);
            setError(err.response?.data?.message || err.message || "Error fetching ledger orders.");
            setOrders([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadPayments = useCallback(async (page) => {
        setLoading(true);
        setError("");
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/market-placer/my-payments`,
                { ...getAuthConfig(), params: { page, per_page: PAGE_SIZE } }
            );
            if (!response.data?.success) {
                throw new Error(response.data?.message || "Failed to load payment history.");
            }
            const result = getPagedResult(response, page);
            setPayments(result.rows);
            setPaymentsPagination(result.pagination);
        } catch (err) {
            console.error("Error fetching market placer ledger payments:", err);
            setError(err.response?.data?.message || err.message || "Error fetching payment history.");
            setPayments([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadSummary = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/market-placer/my-ledger-summary`,
                getAuthConfig()
            );
            if (!response.data?.success) {
                throw new Error(response.data?.message || "Failed to load ledger summary.");
            }
            setSummary(response.data?.data || {});
        } catch (err) {
            console.error("Error fetching market placer ledger summary:", err);
            setError(err.response?.data?.message || err.message || "Error fetching ledger summary.");
        } finally {
            setLoading(false);
        }
    }, []);

    // Summary is needed on every tab for the "Add payment" button.
    useEffect(() => {
        loadSummary();
    }, [loadSummary]);

    useEffect(() => {
        if (activeTab === "orders") loadOrders(ordersPage);
        if (activeTab === "payments") loadPayments(paymentsPage);
        if (activeTab === "summary") loadSummary();
    }, [activeTab, ordersPage, paymentsPage, loadOrders, loadPayments, loadSummary]);

    const visibleOrders = useMemo(() => {
        const keyword = orderSearch.trim().toLowerCase();
        return orders.filter((order) => {
            const isDelivered = (order.delivery_status || "").toLowerCase() === "delivered";
            const matchesTab = activeOrderTab === "delivered" ? isDelivered : !isDelivered;
            const matchesSearch = !keyword || [
                order.order_number,
                order.product_code,
                order.tracking_number,
                order.marketplace_name,
            ].some((value) => String(value || "").toLowerCase().includes(keyword));
            return matchesTab && matchesSearch;
        });
    }, [orders, activeOrderTab, orderSearch]);

    const visiblePayments = useMemo(() => {
        const keyword = paymentSearch.trim().toLowerCase();
        return payments.filter((payment) => {
            const searchable = [
                payment.payment_type,
                payment.bank?.name,
                payment.notes,
            ].some((value) => String(value || "").toLowerCase().includes(keyword));
            const paymentDate = payment.payment_date ? String(payment.payment_date).slice(0, 10) : "";
            const matchesFromDate = !paymentFromDate || paymentDate >= paymentFromDate;
            const matchesToDate = !paymentToDate || paymentDate <= paymentToDate;
            return (!keyword || searchable) && matchesFromDate && matchesToDate;
        });
    }, [payments, paymentSearch, paymentFromDate, paymentToDate]);

    const settlementType = (summary?.settlement_type || "").toLowerCase();
    const userPays = settlementType === "receivable";
    const userReceives = settlementType === "payable";
    const settlementLabels = userPays
        ? {
            total: "Total Amount to Pay",
            settled: "Amount Paid",
            remaining: "Remaining to Pay",
            extra: "Extra Paid",
        }
        : userReceives
            ? {
                total: "Total Amount to Receive",
                settled: "Amount Received",
                remaining: "Remaining to Receive",
                extra: "Extra Received",
            }
            : {
                total: "Total Amount",
                settled: "Settled Amount",
                remaining: "Remaining Amount",
                extra: "Extra Amount",
            };

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Body className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                            <div>
                                <h3 className="fw-bold mb-1">{t("Market Placer Ledger")}</h3>
                                <p className="text-muted mb-0">{t("View your earnings, orders, and payment history.")}</p>
                            </div>
                            {summary?.who_adds_payment === "self" && (
                                <div className="text-end">
                                    <Button
                                        variant="success"
                                        onClick={() => setShowSubmitPayment(true)}
                                        disabled={Number(summary?.remaining_amount || 0) - Number(summary?.pending_amount || 0) <= 0}
                                    >
                                        {t("Add payment you sent")}
                                    </Button>
                                    {Number(summary?.pending_amount || 0) > 0 && (
                                        <div className="small text-muted mt-1">
                                            {t("Waiting for approval")}: PKR {formatAmount(summary.pending_amount)}
                                        </div>
                                    )}
                                </div>
                            )}
                            {summary?.who_adds_payment === "admin" && (
                                <div className="small text-muted text-end" style={{ maxWidth: "280px" }}>
                                    {t("Payable: the company adds the payments it makes to you. They show here once approved.")}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                    {error && <Alert variant="danger">{error}</Alert>}
                    {notice && <Alert variant="success" dismissible onClose={() => setNotice("")}>{notice}</Alert>}
                    <SubmitPaymentModal
                        show={showSubmitPayment}
                        onHide={() => setShowSubmitPayment(false)}
                        summary={summary}
                        banksUrl={`${process.env.REACT_APP_API_URL}/market-placer/payment-banks`}
                        submitUrl={`${process.env.REACT_APP_API_URL}/market-placer/my-payments`}
                        onSuccess={(msg) => {
                            setNotice(msg);
                            loadSummary();
                            loadPayments(1);
                        }}
                        t={t}
                    />
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom-0 pt-3">
                            <Nav variant="tabs" activeKey={activeTab} onSelect={(tab) => tab && setActiveTab(tab)}>
                                <Nav.Item><Nav.Link eventKey="summary">{t("Summary")}</Nav.Link></Nav.Item>
                                <Nav.Item><Nav.Link eventKey="orders">{t("Orders")}</Nav.Link></Nav.Item>
                                <Nav.Item><Nav.Link eventKey="payments">{t("Payments")}</Nav.Link></Nav.Item>
                            </Nav>
                        </Card.Header>
                        {loading ? (
                            <Card.Body className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <div className="small text-muted mt-2">{t("Loading ledger...")}</div>
                            </Card.Body>
                        ) : (
                            <>
                                {activeTab === "summary" && (
                                    <Card.Body className="pt-4">
                                        {summary ? (
                                            <Row className="justify-content-center">
                                                <Col lg={8} xl={7}>
                                                    <Card className="border shadow-sm">
                                                        <Card.Body className="p-0">
                                                            <div className={`p-4 border-bottom ${userPays ? "bg-warning-subtle" : userReceives ? "bg-success-subtle" : "bg-light"}`}>

                                                                <h4 className={`fw-bold mb-1 ${userPays ? "text-warning-emphasis" : userReceives ? "text-success" : "text-dark"}`}>
                                                                    {userPays
                                                                        ? t("You need to pay")
                                                                        : userReceives
                                                                            ? t("You will receive")
                                                                            : t("Your account balance")}
                                                                </h4>

                                                            </div>
                                                            <div className="px-4">
                                                                {[
                                                                    {
                                                                        label: userPays ? "Total amount you owe" : userReceives ? "Total amount owed to you" : settlementLabels.total,
                                                                        value: summary.total_amount,
                                                                    },
                                                                    {
                                                                        label: userPays ? "Already paid" : userReceives ? "Already received" : settlementLabels.settled,
                                                                        value: summary.settled_amount,
                                                                    },
                                                                    {
                                                                        label: userPays ? "Still to pay" : userReceives ? "Still to receive" : settlementLabels.remaining,
                                                                        value: summary.remaining_amount,
                                                                        emphasized: true,
                                                                    },
                                                                    {
                                                                        label: userPays ? "Extra amount you paid" : userReceives ? "Extra amount received" : settlementLabels.extra,
                                                                        value: summary.extra_amount,
                                                                    },
                                                                    { label: "Opening balance", value: summary.opening_balance },
                                                                ].map((item) => (
                                                                    <div
                                                                        key={item.label}
                                                                        className="d-flex justify-content-between align-items-center gap-3 py-3 border-bottom"
                                                                    >
                                                                        <span className={item.emphasized ? "fw-semibold text-dark" : "text-muted"}>
                                                                            {t(item.label)}
                                                                        </span>
                                                                        <span className={`text-nowrap ${item.emphasized ? "fw-bold text-dark fs-5" : "fw-semibold text-dark"}`}>
                                                                            PKR {formatAmount(item.value)}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                                <div className="d-flex justify-content-between align-items-center gap-3 py-3">
                                                                    <span className="text-muted">{t("Delivered orders")}</span>
                                                                    <span className="fw-semibold text-dark">{summary.delivered_orders ?? 0}</span>
                                                                </div>
                                                                <div className="d-flex justify-content-between align-items-center gap-3 pb-3">
                                                                    <span className="text-muted">{t("Pending orders")}</span>
                                                                    <span className="fw-semibold text-dark">{summary.pending_orders ?? 0}</span>
                                                                </div>
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            </Row>
                                        ) : (
                                            <div className="text-muted text-center py-4">{t("No summary data available.")}</div>
                                        )}
                                    </Card.Body>
                                )}
                                {activeTab === "orders" && (
                                    <>
                                        <div className="px-3 pt-3">
                                            <Nav
                                                variant="pills"
                                                activeKey={activeOrderTab}
                                                onSelect={(tab) => tab && setActiveOrderTab(tab)}
                                                className="gap-2"
                                            >
                                                <Nav.Item><Nav.Link eventKey="delivered">{t("Delivered")}</Nav.Link></Nav.Item>
                                                <Nav.Item><Nav.Link eventKey="others">{t("Others")}</Nav.Link></Nav.Item>
                                            </Nav>
                                        </div>
                                        <Card.Body className="pb-3">
                                            <Row className="g-3 align-items-end">
                                                <Col md={6} lg={6}>
                                                    <Form.Group>
                                                        <Form.Label className="small fw-semibold text-muted">{t("Search this page")}</Form.Label>
                                                        <Form.Control
                                                            type="search"
                                                            value={orderSearch}
                                                            onChange={(event) => setOrderSearch(event.target.value)}
                                                            placeholder={t("Order #, product code, tracking...")}
                                                        />
                                                    </Form.Group>
                                                </Col>

                                            </Row>
                                        </Card.Body>
                                        <div className="table-responsive">
                                            <Table hover className="mb-0 align-middle">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>{t("Order #")}</th>
                                                        <th>{t("Product Code")}</th>
                                                        <th>{t("Marketplace")}</th>
                                                        <th>{t("Delivery Status")}</th>
                                                        <th className="text-end">{t("Sell Price (USD)")}</th>
                                                        <th className="text-end">{t("Shipping (PKR)")}</th>
                                                        <th className="text-end">{t("Company Amount (USD)")}</th>
                                                        <th className="text-end">{t("Company Amount (PKR)")}</th>
                                                        <th className="text-end">{t("Earning (USD)")}</th>
                                                        <th className="text-end">{t("Earning (PKR)")}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {visibleOrders.length ? visibleOrders.map((order, index) => (
                                                        <tr key={`${order.order_number}-${order.product_code}-${index}`}>
                                                            <td className="fw-semibold">{order.order_number || "-"}</td>
                                                            <td>{order.product_code || "-"}</td>
                                                            <td>{order.marketplace_name || "-"}</td>
                                                            <td>
                                                                <Badge bg={getStatusVariant(order.delivery_status)}>
                                                                    {order.delivery_status || t("Unknown")}
                                                                </Badge>
                                                            </td>
                                                            <td className="text-end">USD {formatAmount(order.sell_price_usd)}</td>
                                                            <td className="text-end">PKR {formatAmount(order.shipping_pkr)}</td>
                                                            <td className="text-end">USD {formatAmount(order.company_amount_usd)}</td>
                                                            <td className="text-end">PKR {formatAmount(order.company_amount_pkr)}</td>
                                                            <td className="text-end">USD {formatAmount(order.earning_usd)}</td>
                                                            <td className="text-end fw-semibold">PKR {formatAmount(order.earning_pkr)}</td>
                                                        </tr>
                                                    )) : (
                                                        <tr><td colSpan={10} className="text-center text-muted py-4">{t("No orders found on this page.")}</td></tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                        <LedgerPagination pagination={ordersPagination} onPageChange={setOrdersPage} label={t("Orders")} />
                                    </>
                                )}
                                {activeTab === "payments" && (
                                    <>
                                        <Card.Body className="pb-3">
                                            <Row className="g-3">
                                                <Col md={6} lg={4}>
                                                    <Form.Group>
                                                        <Form.Label className="small fw-semibold text-muted">{t("Search this page")}</Form.Label>
                                                        <Form.Control
                                                            type="search"
                                                            value={paymentSearch}
                                                            onChange={(event) => setPaymentSearch(event.target.value)}
                                                            placeholder={t("Payment type, bank, notes...")}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col sm={6} md={3}>
                                                    <Form.Group>
                                                        <Form.Label className="small fw-semibold text-muted">{t("From Date")}</Form.Label>
                                                        <Form.Control type="date" value={paymentFromDate} onChange={(event) => setPaymentFromDate(event.target.value)} />
                                                    </Form.Group>
                                                </Col>
                                                <Col sm={6} md={3}>
                                                    <Form.Group>
                                                        <Form.Label className="small fw-semibold text-muted">{t("To Date")}</Form.Label>
                                                        <Form.Control type="date" value={paymentToDate} onChange={(event) => setPaymentToDate(event.target.value)} />
                                                    </Form.Group>
                                                </Col>
                                                <Col xs={12} className="small text-muted">
                                                    {t("Filters apply to the records on this page.")}
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                        <div className="table-responsive">
                                            <Table hover className="mb-0 align-middle">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>{t("Payment Date")}</th>
                                                        <th>{t("Status")}</th>
                                                        <th>{t("Bank")}</th>
                                                        <th>{t("Notes")}</th>
                                                        <th className="text-end">{t("Amount")}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {visiblePayments.length ? visiblePayments.map((payment, index) => (
                                                        <tr key={`${payment.payment_date}-${index}`}>
                                                            <td>{formatDate(payment.payment_date)}</td>
                                                            <td>
                                                                <Badge bg={getPaymentStatusVariant(payment)}>
                                                                    {t(getPaymentStatus(payment))}
                                                                </Badge>
                                                            </td>
                                                            <td>{payment.bank?.name || "-"}</td>
                                                            <td>{payment.notes || "-"}</td>
                                                            <td className="text-end fw-semibold">PKR {formatAmount(payment.amount)}</td>
                                                        </tr>
                                                    )) : (
                                                        <tr><td colSpan={5} className="text-center text-muted py-4">{t("No payments found on this page.")}</td></tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                        <LedgerPagination pagination={paymentsPagination} onPageChange={setPaymentsPage} label={t("Payments")} />
                                    </>
                                )}
                            </>
                        )}
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}
